import { catchError, finalize, switchMap } from "rxjs/operators";
import { inject } from "@angular/core";
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaderResponse,
  HttpInterceptorFn,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent,
} from "@angular/common/http";
import { Router } from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { AuthToken } from "@company-box/models";
import { AUTH_SVC, AuthService } from "@company-box/services";
import { BehaviorSubject, from, Observable, throwError } from "rxjs";

let isRefreshingToken = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const refreshTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  return next(req).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        switch ((error as HttpErrorResponse).status) {
          case 500:
            return handle500Error(req, next, error);
          case 400:
            return handle400Error(error);
          case 401:
            return handle401Error(req, next);
          default:
            return throwError(() => error);
        }
      } else {
        return throwError(() => error);
      }
    })
  );
};

/*
 * TODO: Ugly hack for getting around oauth error from secondary service, call to ProjectService calls
 * UserService to validate token and gets "invalid_token" response
 * This should be handled in ProjectService as a OAuth2 client but it seems to be complicated to get it working
 */
const handle500Error = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  error: HttpErrorResponse
): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> => {
  if (error.error.message.trim() === "400") {
    return handle401Error(req, next);
  }
  return throwError(() => error);
};

const handle400Error = (
  error: HttpErrorResponse
): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> => {
  if (
    error &&
    error.status === 400 &&
    error.error &&
    (error.error.error === "invalid_grant" || error.error.error === "invalid_token")
  ) {
    // If we get a 400 and the error message is 'invalid_grant', the token is no longer valid so logout.
    (inject(AUTH_SVC) as AuthService).clearToken();

    // Route to the login page (implementation up to you)
    inject(Router).navigate(["/login"]);
  }
  return throwError(() => error);
};

const handle401Error = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> => {
  const authService = inject(AUTH_SVC) as AuthService;
  const token = authService.getToken();
  const logger = inject(NGXLogger);

  if (!token) {
    logger.error("401 not refreshing token");
    // If there is no token and we get a 401 force new login
    refreshTokenSubject.next(null);
    logoutUser();
    return throwError(() => new Error("401 not refreshing token"));
  } else if (!isRefreshingToken) {
    isRefreshingToken = true;

    // Reset here so that the following requests wait until the token
    // comes back from the refreshToken call.
    refreshTokenSubject.next(null);

    return from((authService as AuthService).renewToken()).pipe(
      switchMap((newToken: AuthToken) => {
        if (newToken && newToken.accessToken) {
          logger.info("refreshed token");
          refreshTokenSubject.next(newToken);
          return next(req);
        }

        logger.error("error refreshing token: any");
        // If we don't get a new token, we are in trouble so logout.
        logoutUser();
        const err = new Error("error refreshing token: any");
        return throwError(() => err);
      }),
      catchError((error: any) => {
        // If there is an exception calling 'refreshToken', bad news so logout.
        logger.error("error refreshing token", error);
        logoutUser();
        return throwError(() => error);
      }),
      finalize(() => {
        isRefreshingToken = false;
      })
    );
  }
  return next(req);
};

const logoutUser = () => {
  inject(NGXLogger).trace("clear token and request login");

  const authService = inject(AUTH_SVC) as AuthService;
  authService.clearToken();

  // Route to the login page (implementation up to you)
  inject(Router).navigate(["/login"]);
};
